import $ from 'jquery';
import imageMapResize from 'image-map-resizer';
import SyosEvents from './utils/SyosEvents';
import templateScreenButtonListItem from './templates/screen-button-list-item';
import SyosUiContainer from './SyosUiContainer';
import Click from './utils/Click';
import GestureList from './utils/GestureList';
import SyosPageComponent from './components/SyosPageComponent';
import SyosScreenButton from './SyosScreenButton';
import type SyosController from './SyosController';
import { Screen } from './api/types';
import SyosConfig from './SyosConfig';
import parseZoneIdFromScreenRedirectUrl from './utils/parseZoneIdFromScreenRedirectUrl';

const Class = {
  MAP_CONTAINER: 'tn-syos-screen-container__map-container',
  BTN_LIST: 'tn-syos-screen-container__button-list',
  BTN_LIST_CONTAINER: 'tn-syos-screen-container__button-list-container',
  BTN_LIST_CONTAINER_STANDALONE_MODIFIER: 'tn-syos-screen-container__button-list-container--standalone',
};

const sectionListId = 'tn-syos-screen-list';

export function getComponentElements(rootHeadingLevel: number) {
  const { labelSelectASection } = SyosPageComponent;
  const headingId = 'tn-syos-screen-list-heading';

  const headingTag = `h${rootHeadingLevel + 1}`;

  const el = document.createElement('div');

  el.innerHTML = `
    <div class="${Class.BTN_LIST_CONTAINER}">
      <${headingTag} class="${Class.BTN_LIST}-heading" id="${headingId}">${labelSelectASection}</${headingTag}>
      <ul class="${Class.BTN_LIST}" aria-labelledby="${headingId}" id="${sectionListId}"></ul>
    </div>
    <div class="${Class.MAP_CONTAINER}"></div>
  `;

  const listHeading = el.querySelector(`#${headingId}`)!;

  const mapContainer = el.querySelector(`.${Class.MAP_CONTAINER}`)!;

  const btnList = el.querySelector(`.${Class.BTN_LIST}`)!;

  const btnListContainer = el.querySelector(`.${Class.BTN_LIST_CONTAINER}`)!;

  return {
    listHeading,
    mapContainer,
    btnList,
    btnListContainer,
  };
}

class SyosScreenContainer extends SyosUiContainer {
  static sectionListId = sectionListId;

  buttons: Map<number, SyosScreenButton>;

  $mapContainer: JQuery<Element>;

  $btnList: JQuery<Element>;

  $btnListContainer: JQuery<Element>;

  $customScreen?: JQuery<Element>;

  gestures?: GestureList;

  currentScreen?: Screen;

  constructor(syosController: SyosController, config: SyosConfig) {
    super(syosController, '.tn-syos-screen-container');

    const {
      mapContainer, btnList, btnListContainer,
    } = getComponentElements(config.rootHeadingLevel);

    this.$el?.append(btnListContainer);
    this.$el?.append(mapContainer);

    this.$mapContainer = $(mapContainer);
    this.$btnList = $(btnList);
    this.$btnListContainer = $(btnListContainer);

    this.$mapContainer.hide();
    this.$btnListContainer.hide();

    this.buttons = new Map();

    syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);
  }

  onScreenUpdated = ({ screen }: { screen: Screen }) => {
    if (!screen) {
      this.show();

      if (this.currentScreen) {
        const btn = this.buttons.get(this.currentScreen.id)!;
        btn.focus();
      }
    }

    this.currentScreen = screen;
  };

  onSeatListDataLoaded = () => {
    this.$el?.css({ display: 'none' });
  };

  setData(screenData: Screen[]) {
    this.syosController?.hideSeatContainer();
    this.draw(screenData);
  }

  selectScreen(screen: Screen) {
    if (!screen.isAvailable) {
      return;
    }

    if (screen.isSyos) {
      this.syosController?.setCurrentScreen(screen);
    } else {
      const zoneId = parseZoneIdFromScreenRedirectUrl(screen.redirectUrl);

      this.syosController?.redirectToBestAvailable(zoneId);
    }
  }

  draw(screens: Screen[]) {
    /*
    <script id="smap-4-template" ...
      <img name="smap4" id="smap4" usemap="#m_smap4" src="foo.gif" ...
      <map name="m_smap4" id="m_smap4">
        <area id="smap-4-screen-1a" screenId="syos-id-1" class="smap-4-screen-1" alt="screen-1" ...
        <area id="smap-4-screen-1b" ...
        ...
      </map>
    </script>
    */

    const firstScreen = screens[0];
    const mapNumber = firstScreen.seatMapId;
    const screenButtonIsSectionHeadingOnly = !firstScreen.screenButtonShouldDisplayPriceTypeDescription
      && !firstScreen.screenButtonShouldRenderPriceRange;

    this.$customScreen = $(`#smap-${mapNumber}-template`); // #smap-4-template

    if (this.getIsUsingCustomBirdsEye()) {
      this.$mapContainer.append(this.$customScreen.html());
    } else {
      this.$btnListContainer.addClass(Class.BTN_LIST_CONTAINER_STANDALONE_MODIFIER);
      this.$mapContainer.remove();
    }

    const clickHandlers: Click[] = [];

    screens.forEach((screen) => {
      const screenId = screen.id;
      const screenClass = `.smap-${mapNumber}-screen-${screenId}`; // .smap-4-screen-1

      const onClick = () => this.selectScreen(screen);

      const $screens = $(screenClass);
      if ($screens.length) {
        // There could be multiple UI elements representing a given screen
        $screens.each((index, screenEl) => {
          const handler = new Click(screenEl, onClick);
          clickHandlers.push(handler);
        });
      }

      const $listItem = $(templateScreenButtonListItem.render(screen, screenButtonIsSectionHeadingOnly));
      const $btn = $listItem.find('button');

      const btn = new SyosScreenButton($btn[0], screen, onClick);

      this.buttons.set(screenId, btn);

      this.$btnList.append($listItem);
    });

    this.gestures = new GestureList(clickHandlers).start();

    this.$mapContainer.find('img').attr('alt', firstScreen.facilityAltText);

    const $maps = this.$mapContainer.find('map');
    if ($maps.length) {
      imageMapResize($maps[0]);

      const $imageMapAreas = $maps.find('area');
      $imageMapAreas.attr('aria-hidden', true);

      $imageMapAreas.toArray().forEach((area) => {
        const $area = $(area);
        let screenId = $area.attr('screenid');
        if (screenId !== null) {
          screenId = screenId.replace(/[^\d]/g, '');
          screenId = parseInt(screenId, 10);
          $area.attr('data-tn-screen-id', screenId);
        }
      });
    }

    const screenId = this.syosController?.getScreenId();

    if (screenId) {
      const screenToSelect = screens.find((screen) => screen.id === screenId);

      if (screenToSelect && screenToSelect.isAvailable) {
        this.selectScreen(screenToSelect);

        return;
      }
    }

    this.show();
  }

  getIsUsingCustomBirdsEye() {
    if (this.$customScreen) {
      return this.$customScreen.length > 0;
    }

    return false;
  }

  show() {
    const $els = this.$mapContainer.add(this.$btnListContainer);
    $els.css({ display: '', opacity: 0 });
    this.$el?.css({ display: '' });
    $els.animate({ opacity: 1 });
  }

  destroy() {
    this.buttons.forEach((btn) => btn.destroy());

    this.buttons.clear();

    this.gestures?.destroy();
    this.gestures = undefined;

    super.destroy();
  }
}

export default SyosScreenContainer;
